<template>
    <ul class="legend-container">
        <li class="legend-row"
            v-tooltip="{ content: getTooltipText(option), delay: 500 }"
            v-for="(option, optionIndex) in options"
            :key="option.id"
            @mouseenter="highlightColumn(optionIndex)"
            @mouseleave="clearHighlight()">
            <label :class="{ disabled: isOptionDisabled(option) }">
                <input type="checkbox"
                       :checked="selected.includes(option.id)"
                       :style="selected.includes(option.id) && {
                         backgroundColor: colors[optionIndex],
                         borderColor: colors[optionIndex]
                       }"
                       :disabled="isOptionDisabled(option)"
                       @change="toggleColumn($event, option)" />
                <span class="legend-option-text"
                      :class="{
                         'font-6': optionIndex === 0,
                         'font-7': optionIndex > 0
                      }"
                >{{option.label || option.id}}</span>
            </label>
        </li>
    </ul>
</template>

<script>
import Vue from 'vue';
import tooltip from 'services/libWrappers/vue-tooltip';

tooltip(Vue);

export default {
    name: 'Legend',
    props: {
        options: {
            type: Array,
            required: true
        },
        selected: {
            type: Array,
            required: true
        },
        colors: {
            type: Array,
            required: true
        },
        maxSelected: {
            type: Number
        }
    },
    methods: {
        highlightColumn(optionIndex) {
            this.$emit('highlight', optionIndex);
        },
        clearHighlight() {
            this.$emit('clearHighlight');
        },
        toggleColumn(event, option) {
            this.$emit('checked', {
                option: option.id,
                checked: event.target.checked
            });
        },
        doesOptionSelectionPassesMax(option) {
            const optionSelected = this.selected.includes(option.id);

            return this.selected.length === this.maxSelected && !optionSelected;
        },
        isOptionDisabled(option) {
            const optionSelected = this.selected.includes(option.id);
            const optionSelectionPassesMax = this.doesOptionSelectionPassesMax(option);
            const singleOptionAvailable = optionSelected && this.selected.length === 1;

            return optionSelectionPassesMax || singleOptionAvailable;
        },
        getTooltipText(option) {
            return this.doesOptionSelectionPassesMax(option)
                ? `You can only select up to ${this.maxSelected} items` : '';
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide";
@import "~components/Tooltip/tooltip.css";

.legend-container {
    list-style-type: none;
    margin-block-start: 0;
    margin-block-end: 0;
    padding-inline-start: 0;
    overflow: auto;
    width: 213px;
    height: 379px;
    border: 1px solid #E1E6EB;
    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;
    padding: 0 10px;
}

.legend-option-text {
    margin-left: 8px;
}

.legend-row {
    width: 100%;
    padding: 10px 0;
    border-bottom: 1px solid #E1E6EB;
    word-break: break-word;
    white-space: pre-line;
    overflow: hidden;

    &:last-child {
        border-bottom: none;
    }

    label {
        cursor: pointer;
        display: flex;
        align-items: center;
        user-select: none;

        &.disabled {
            cursor: default;
            .legend-option-text {
                color: #678099;
                cursor: default;
            }
        }
    }

    input:before {
        content: none;
    }
}

input[type="checkbox"] {
    min-width: 15px;
    width: 15px;
    height: 15px;
    border-radius: 3px;
    appearance: none;
    border: 1px solid $light;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    outline: none;
}

input[type="checkbox"]:not(:disabled) {
    cursor: pointer;
}

input[type="checkbox"]:checked {
    background: url(data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2010%208%22%3E%0A%20%20%20%20%3Cpath%20fill%3D%22%23FFF%22%20fill-rule%3D%22evenodd%22%20d%3D%22M9.395%201.852l-4.469%204.47-.84.839a.572.572%200%200%201-.42.173.571.571%200%200%201-.419-.173l-.84-.84L.173%204.087A.571.571%200%200%201%200%203.667c0-.165.058-.305.173-.42l.84-.84a.571.571%200%200%201%20.42-.172c.164%200%20.304.057.419.172L3.667%204.23%207.717.173A.571.571%200%200%201%208.136%200c.164%200%20.304.058.419.173l.84.84a.571.571%200%200%201%20.172.42.571.571%200%200%201-.173.419z%22/%3E%0A%3C/svg%3E%0A) no-repeat 2px 3px;
    background-color: inherit;
    background-size: 9px 8px;
}
</style>
